import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import { format } from "date-fns";
import {
  FileText, X, Search, Loader2,
  Unlink, Link2, AlertCircle, Maximize2, Minimize2, Eye, Save, BookmarkPlus
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { TemplateTrainer } from "@/components/template-trainer";

interface ReceiptPreviewPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry: any;
  statementId: number;
  onRefetch: () => void;
}

function tryParseDate(dateStr: string): string {
  try {
    const ddmmyyyy = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyy) {
      const d = new Date(parseInt(ddmmyyyy[3]), parseInt(ddmmyyyy[2]) - 1, parseInt(ddmmyyyy[1]));
      return format(d, "MMM d, yyyy");
    }
    return format(new Date(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

function ReceiptEditForm({ receiptId, onSaved }: { receiptId: number; onSaved: () => void }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  const { data: receipt, isLoading } = useQuery({
    queryKey: ["/api/receipts", receiptId],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/receipts/${receiptId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load receipt");
      return res.json();
    },
    enabled: !!receiptId,
  });

  const [vendor, setVendor] = useState("");
  const [amount, setAmount] = useState("");
  const [receiptDate, setReceiptDate] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [vatAmount, setVatAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");

  useEffect(() => {
    if (receipt) {
      setVendor(receipt.vendor || "");
      setAmount(receipt.amount?.toString() || "");
      setReceiptDate(receipt.receiptDate || "");
      setCurrency(receipt.currency || "GBP");
      setVatAmount(receipt.vatAmount?.toString() || "");
      setInvoiceNumber(receipt.invoiceNumber || "");
    }
  }, [receipt]);

  const updateMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await fetch(`${BASE}/api/receipts/${receiptId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Update failed" }));
        throw new Error(err.error || "Failed to update receipt");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/receipts", receiptId] });
      toast({ title: "Receipt updated" });
      onSaved();
    },
    onError: (error: Error) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const handleSave = () => {
    updateMutation.mutate({
      vendor: vendor.trim(),
      amount: amount ? parseFloat(amount) : null,
      receiptDate: receiptDate.trim(),
      currency,
      vatAmount: vatAmount ? parseFloat(vatAmount) : null,
      invoiceNumber: invoiceNumber.trim(),
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Vendor</label>
        <input
          type="text"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          placeholder="e.g. Wickes, Tri-wall"
          className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Amount</label>
          <input
            type="number"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Currency</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white"
          >
            <option value="GBP">GBP (£)</option>
            <option value="USD">USD ($)</option>
            <option value="EUR">EUR (€)</option>
            <option value="CAD">CAD</option>
            <option value="AUD">AUD</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
        <input
          type="text"
          value={receiptDate}
          onChange={(e) => setReceiptDate(e.target.value)}
          placeholder="e.g. 16/02/2026"
          className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">VAT</label>
          <input
            type="number"
            step="0.01"
            value={vatAmount}
            onChange={(e) => setVatAmount(e.target.value)}
            placeholder="0.00"
            className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Invoice #</label>
          <input
            type="text"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            placeholder="Optional"
            className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>
      </div>
      <Button onClick={handleSave} disabled={updateMutation.isPending} size="sm" className="w-full gap-1.5">
        {updateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        {updateMutation.isPending ? "Saving…" : "Save & Re-match"}
      </Button>
      <p className="text-[10px] text-slate-400 text-center">
        {String(receipt?.originalFilename || '')}
      </p>
    </div>
  );
}

export function ReceiptPreviewPanel({ open, onOpenChange, entry, statementId, onRefetch }: ReceiptPreviewPanelProps) {
  const { toast } = useToast();
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [unmatching, setUnmatching] = useState(false);
  const [receiptSearch, setReceiptSearch] = useState("");
  const [previewReceiptId, setPreviewReceiptId] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [trainerOpen, setTrainerOpen] = useState(false);
  const [trainerReceiptId, setTrainerReceiptId] = useState<number | null>(null);

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  const receipts = entry?.matched
    ? (entry.matchedReceipts?.length > 0 ? entry.matchedReceipts : entry.matchedReceipt ? [entry.matchedReceipt] : [])
    : [];

  const activePreviewId = previewReceiptId || (receipts.length > 0 ? receipts[0].id : null);

  const { data: unmatchedReceipts, isLoading: loadingUnmatched, refetch: refetchUnmatched } = useQuery({
    queryKey: ["/api/receipts", "unmatched"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/receipts?matched=false`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load receipts");
      return res.json();
    },
    enabled: open && !entry?.matched,
  });

  useEffect(() => {
    if (open && !entry?.matched) {
      void refetchUnmatched();
    }
  }, [open, entry?.id, entry?.matched, refetchUnmatched]);

  const trainerTargetId = trainerReceiptId || activePreviewId || previewReceiptId;
  const { data: trainerReceipt } = useQuery({
    queryKey: ["/api/receipts", trainerTargetId],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/receipts/${trainerTargetId}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load receipt");
      return res.json();
    },
    enabled: trainerOpen && !!trainerTargetId,
  });

  useEffect(() => {
    if (!open) {
      setPreviewReceiptId(null);
      setReceiptSearch("");
      setIsFullscreen(false);
      setTrainerOpen(false);
      setTrainerReceiptId(null);
    }
  }, [open]);

  const handleUnmatch = async () => {
    setUnmatching(true);
    try {
      const res = await fetch(`${BASE}/api/statements/${statementId}/entries/${entry.id}/unmatch`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to unmatch");
      toast({ title: "Match removed" });
      onRefetch();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Failed to unmatch", description: err.message, variant: "destructive" });
    } finally {
      setUnmatching(false);
    }
  };

  const handleAssign = async (receiptId: number) => {
    setAssigningId(receiptId);
    try {
      const res = await fetch(`${BASE}/api/statements/${statementId}/entries/${entry.id}/assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ receiptId }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Assign failed" }));
        throw new Error(err.error || "Failed to assign receipt");
      }
      toast({ title: "Receipt assigned successfully" });
      onRefetch();
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: "Failed to assign", description: err.message, variant: "destructive" });
    } finally {
      setAssigningId(null);
    }
  };

  const filteredUnmatched = (unmatchedReceipts || []).filter((r: any) => {
    if (!receiptSearch) return true;
    const q = receiptSearch.toLowerCase();
    return (
      (r.vendor || "").toLowerCase().includes(q) ||
      (r.originalFilename || "").toLowerCase().includes(q) ||
      (r.invoiceNumber || "").toLowerCase().includes(q) ||
      (r.amount?.toString() || "").includes(q)
    );
  });
  const hasSearch = receiptSearch.trim().length > 0;
  const totalUnmatched = (unmatchedReceipts || []).length;

  if (!entry) return null;

  const isImage = (filename: string) => /\.(png|jpe?g|gif|webp)$/i.test(filename || "");

  const renderPreview = (receiptId: number, filename?: string) => (
    <div className="flex-1 min-h-0 bg-slate-100">
      {isImage(filename || "") ? (
        <img
          src={`${BASE}/api/receipts/${receiptId}/preview`}
          alt="Receipt"
          className="w-full h-full object-contain"
        />
      ) : (
        <iframe
          src={`${BASE}/api/receipts/${receiptId}/preview`}
          className="w-full h-full border-0"
          title="Receipt preview"
        />
      )}
    </div>
  );

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-[90vw] lg:max-w-[80vw] p-0 flex flex-col">
          <SheetHeader className="px-6 py-4 border-b border-slate-200 bg-slate-50 shrink-0">
            <SheetTitle className="text-lg font-display">
              {String(entry.description || '')}
            </SheetTitle>
            <SheetDescription className="flex items-center gap-3 text-sm flex-wrap">
              <span>{tryParseDate(String(entry.transactionDate || ''))}</span>
              <span className="text-slate-300">|</span>
              <span className="font-semibold text-slate-900">{formatCurrency(Number(entry.amount) || 0)}</span>
              <span className="text-slate-300">|</span>
              <span>{String(entry.cardholderName || '')}</span>
              {entry.matched ? (
                <Badge variant="success" className="ml-2 text-xs">Matched</Badge>
              ) : (
                <Badge variant="warning" className="ml-2 text-xs">Unmatched</Badge>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 min-h-0 flex">
            {entry.matched && activePreviewId ? (
              <div className="flex flex-1 min-h-0">
                <div className="flex-1 min-w-0 flex flex-col border-r border-slate-200">
                  <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between shrink-0">
                    <span className="text-sm font-medium text-slate-600">Receipt Preview</span>
                    <button
                      onClick={() => setIsFullscreen(true)}
                      className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                      title="Full screen"
                    >
                      <Maximize2 className="w-4 h-4" />
                    </button>
                  </div>
                  {renderPreview(activePreviewId, receipts.find((r: any) => r.id === activePreviewId)?.originalFilename)}
                </div>

                <div className="w-80 shrink-0 flex flex-col overflow-hidden bg-white">
                  <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 shrink-0">
                    <span className="text-sm font-medium text-slate-600">
                      {receipts.length > 1 ? `Matched Receipts (${receipts.length})` : "Receipt Details"}
                    </span>
                  </div>

                  <div className="flex-1 overflow-y-auto p-4">
                    {receipts.length > 1 && (
                      <div className="space-y-2 mb-4 pb-4 border-b border-slate-200">
                        {receipts.map((receipt: any) => (
                          <button
                            key={receipt.id}
                            onClick={() => setPreviewReceiptId(receipt.id)}
                            className={`w-full text-left p-2.5 rounded-lg border text-xs transition-colors ${
                              activePreviewId === receipt.id
                                ? "border-primary bg-primary/5"
                                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <FileText className="w-3.5 h-3.5 text-green-600 shrink-0" />
                              <span className="font-medium text-slate-900 truncate">
                                {String(receipt.vendor || receipt.originalFilename || '')}
                              </span>
                            </div>
                            {receipt.amount != null && (
                              <span className="text-slate-500 ml-5">
                                {formatCurrency(Number(receipt.amount) || 0, String(receipt.currency || "GBP"))}
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    <ReceiptEditForm
                      key={activePreviewId}
                      receiptId={activePreviewId}
                      onSaved={onRefetch}
                    />
                  </div>

                  <div className="p-4 border-t border-slate-200 shrink-0 space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setTrainerReceiptId(activePreviewId); setTrainerOpen(true); }}
                      className="w-full"
                    >
                      <BookmarkPlus className="w-4 h-4 mr-2" />
                      Train Template
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleUnmatch}
                      disabled={unmatching}
                      className="w-full"
                    >
                      {unmatching ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Unlink className="w-4 h-4 mr-2" />
                      )}
                      {unmatching ? "Removing…" : "Remove Match"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-1 min-h-0">
                {previewReceiptId ? (
                  <div className="flex flex-1 min-h-0">
                    <div className="flex-1 min-w-0 flex flex-col border-r border-slate-200">
                      <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 flex items-center justify-between shrink-0">
                        <span className="text-sm font-medium text-slate-600">Receipt Preview</span>
                        <button
                          onClick={() => setIsFullscreen(true)}
                          className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                          title="Full screen"
                        >
                          <Maximize2 className="w-4 h-4" />
                        </button>
                      </div>
                      {renderPreview(
                        previewReceiptId,
                        filteredUnmatched.find((r: any) => r.id === previewReceiptId)?.originalFilename
                      )}
                    </div>

                    <div className="w-72 shrink-0 flex flex-col overflow-hidden bg-white border-r border-slate-200">
                      <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 shrink-0">
                        <span className="text-sm font-medium text-slate-600">Receipt Details</span>
                      </div>
                      <div className="flex-1 overflow-y-auto p-3">
                        <ReceiptEditForm
                          key={previewReceiptId}
                          receiptId={previewReceiptId}
                          onSaved={onRefetch}
                        />
                      </div>
                      <div className="p-3 border-t border-slate-200 shrink-0 space-y-2">
                        <Button
                          size="sm"
                          onClick={() => handleAssign(previewReceiptId)}
                          disabled={assigningId === previewReceiptId}
                          className="w-full gap-1.5"
                        >
                          {assigningId === previewReceiptId ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Link2 className="w-3.5 h-3.5" />
                          )}
                          {assigningId === previewReceiptId ? "Assigning…" : "Assign This Receipt"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setTrainerReceiptId(previewReceiptId); setTrainerOpen(true); }}
                          className="w-full gap-1.5"
                        >
                          <BookmarkPlus className="w-3.5 h-3.5" />
                          Train Template
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1 flex items-center justify-center bg-slate-50 border-r border-slate-200">
                    <div className="text-center text-slate-400 px-6">
                      <Eye className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="font-medium text-slate-500">Select a receipt to preview</p>
                      <p className="text-sm mt-1">Pick from the list on the right to see it here</p>
                    </div>
                  </div>
                )}

                <div className="w-80 shrink-0 flex flex-col overflow-hidden bg-white">
                  <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50/70 shrink-0">
                    <span className="text-sm font-medium text-slate-600">Available Receipts</span>
                  </div>
                  <div className="px-3 py-2.5 border-b border-slate-100 shrink-0">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search vendor, filename, amount…"
                        value={receiptSearch}
                        onChange={(e) => setReceiptSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {loadingUnmatched ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                      </div>
                    ) : filteredUnmatched.length === 0 ? (
                      <div className="px-4 py-12 text-center text-slate-400">
                        <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                        {hasSearch && totalUnmatched > 0 ? (
                          <>
                            <p className="text-sm font-medium text-slate-500">No receipts match "{receiptSearch}"</p>
                            <p className="text-xs mt-1">{totalUnmatched} unmatched receipt{totalUnmatched === 1 ? "" : "s"} available — try a different search</p>
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-slate-500">No unmatched receipts</p>
                            <p className="text-xs mt-1">Upload receipts first, then match them here</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {filteredUnmatched.map((receipt: any) => (
                          <div
                            key={receipt.id}
                            className={`px-3 py-2.5 hover:bg-slate-50 transition-colors cursor-pointer ${
                              previewReceiptId === receipt.id ? "bg-primary/5 border-l-2 border-l-primary" : ""
                            }`}
                            onClick={() => setPreviewReceiptId(receipt.id)}
                          >
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {String(receipt.vendor || receipt.originalFilename || '')}
                            </p>
                            <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                              {receipt.amount != null && (
                                <span className="font-medium">{formatCurrency(Number(receipt.amount) || 0, String(receipt.currency || "GBP"))}</span>
                              )}
                              {receipt.receiptDate && (
                                <>
                                  <span className="text-slate-300">|</span>
                                  <span>{tryParseDate(String(receipt.receiptDate))}</span>
                                </>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {trainerOpen && trainerTargetId && trainerReceipt && (
        <TemplateTrainer
          open={trainerOpen}
          onClose={() => { setTrainerOpen(false); setTrainerReceiptId(null); }}
          rawText={trainerReceipt.rawText || ""}
          previewUrl={`${BASE}/api/receipts/${trainerTargetId}/preview`}
          isImage={/\.(png|jpe?g|gif|webp)$/i.test(trainerReceipt.originalFilename || "")}
          vendor={trainerReceipt.vendor || ""}
          currency={trainerReceipt.currency || "GBP"}
          receiptId={trainerTargetId}
        />
      )}

      {isFullscreen && (activePreviewId || previewReceiptId) && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex flex-col" onClick={() => setIsFullscreen(false)}>
          <div className="flex items-center justify-between px-4 py-3 bg-slate-900 shrink-0">
            <span className="text-sm font-medium text-white">Receipt Preview</span>
            <button
              onClick={() => setIsFullscreen(false)}
              className="p-2 rounded-lg hover:bg-slate-700 text-white transition-colors"
              title="Exit full screen"
            >
              <Minimize2 className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 min-h-0" onClick={(e) => e.stopPropagation()}>
            {renderPreview(
              (activePreviewId || previewReceiptId)!,
              receipts.find((r: any) => r.id === (activePreviewId || previewReceiptId))?.originalFilename
            )}
          </div>
        </div>
      )}
    </>
  );
}
